import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getIcon } from '@/lib/iconMap';
import type { Tables } from '@/integrations/supabase/helpers';
import type { LinkItem } from '@/data/links';
import { CommunityIcon } from '@/components/icons/CommunityIcon';
import { CodeIcon } from '@/components/icons/CodeIcon';
import { BrainIcon } from '@/components/icons/BrainIcon';
import { MediaIcon } from '@/components/icons/MediaIcon';
import { Globe } from 'lucide-react';

type SectionRow = Tables<'sections'>;
type LinkRow = Tables<'links'>;
type ConfigRow = Tables<'site_config'>;

/**
 * Maps a known section ID to its custom SVG icon component.
 * Falls back to the emoji string when the ID is not in the map.
 */
function getSectionIcon(sectionId: string, fallbackEmoji: string): ReactNode {
  const iconMap: Record<string, ReactNode> = {
    'community': <CommunityIcon size={22} />,
    'vibe-coding': <CodeIcon size={22} />,
    'models-infra': <BrainIcon size={22} />,
    'media': <MediaIcon size={22} />,
    'about': <Globe size={22} className="text-cyan-400" />,
    'על-האתר': <Globe size={22} className="text-cyan-400" />,
  };
  return iconMap[sectionId] ?? fallbackEmoji;
}

/** A section row enriched with its resolved icon and full list of child links. */
export interface SectionWithLinks {
  id: string;
  title: string;
  emoji: ReactNode;
  sort_order: number;
  links: LinkItem[];
}

/** Shape returned by usePublicData. */
export interface PublicData {
  config: ConfigRow | null;
  sections: SectionWithLinks[];
  loading: boolean;
  error: string | null;
  /** A successful DB response (or authoritative server DB snapshot) exists, even if empty. */
  loaded: boolean;
  /** Server-rendered initial content is available while the client verifies it. */
  hasInitialData: boolean;
  /** Static content is allowed only when no managed backend is configured. */
  fallbackAllowed: boolean;
  /** Trigger a manual re-fetch after a previous failure. */
  retry: () => void;
}

/** Converts a raw DB link row into the UI-friendly LinkItem shape. */
function toLinkItem(row: LinkRow): LinkItem {
  return {
    id: row.id,
    title: row.title,
    subtitle: row.subtitle,
    description: row.description,
    url: row.url,
    icon: getIcon(row.icon_name),
    color: row.color,
    animation: row.animation as LinkItem['animation'],
    faviconUrl: row.favicon_url || undefined,
    affiliateBenefit: row.affiliate_benefit || undefined,
    tag: (row.tag as LinkItem['tag']) || undefined,
  };
}

/** Read the same public snapshot that produced the HTML, without waiting for a client request. */
const backendConfigured = () => Boolean(supabase || import.meta.env.VITE_SUPABASE_URL || import.meta.env.VITE_SUPABASE_ANON_KEY);

function readInitialPublicData(): { config: ConfigRow | null; sections: SectionWithLinks[]; source: string } | null {
  if (typeof document === 'undefined') return null;
  try {
    const text = document.getElementById('nvision-initial-catalog')?.textContent;
    if (!text) return null;
    const seed = JSON.parse(text);
    if (!seed || !['database', 'fallback', 'unavailable'].includes(seed.source) || !Array.isArray(seed.sections) || !Array.isArray(seed.links)) return null;
    if (seed.source === 'unavailable') return { source: 'unavailable', config: null, sections: [] };
    if (seed.source === 'fallback' && backendConfigured()) return null;
    const sectionRows: SectionRow[] = seed.sections;
    const linkRows: LinkRow[] = seed.links;
    if (sectionRows.some((row) => !row || typeof row.id !== 'string' || typeof row.title !== 'string')) return null;
    if (linkRows.some((row) => !row || typeof row.id !== 'string' || typeof row.section_id !== 'string' || typeof row.title !== 'string' || typeof row.url !== 'string' || typeof row.icon_name !== 'string' || !/^https?:\/\//.test(row.url))) return null;
    return {
      source: seed.source,
      config: seed.config && typeof seed.config === 'object' && !Array.isArray(seed.config) ? seed.config : null,
      sections: sectionRows.filter((row) => row.is_visible !== false).map((row, index) => ({
        id: row.id, title: row.title, sort_order: row.sort_order ?? index,
        emoji: getSectionIcon(row.id, row.emoji || ''),
        links: linkRows.filter((link) => link.section_id === row.id && link.is_visible !== false).map((link) => toLinkItem({
          ...link, subtitle: link.subtitle || '', description: link.description || '', color: link.color || '#8FE3F0', animation: link.animation || 'float',
        })),
      })),
    };
  } catch { return null; }
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

/**
 * Fetches site config, sections, and links from Supabase.
 * Retries up to 3 times on failure, then exposes the error for display.
 * Also subscribes to realtime changes so the UI auto-updates without refresh.
 *
 * @returns config, sections with links, loading/error state, and a retry function.
 */
export function usePublicData(): PublicData {
  const [initialData] = useState(readInitialPublicData);
  const fallbackAllowed = !backendConfigured() && initialData?.source !== 'unavailable';
  const [config, setConfig] = useState<ConfigRow | null>(initialData?.config ?? null);
  const [sections, setSections] = useState<SectionWithLinks[]>(initialData?.sections ?? []);
  const [loaded, setLoaded] = useState(initialData?.source === 'database');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  const requestRef = useRef(0);

  const fetchAll = useCallback(async (attempt: number = 0) => {
    const request = ++requestRef.current;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!supabase) {
      if (!fallbackAllowed && initialData?.source !== 'database') setError('התוכן אינו זמין כרגע. נסו שוב.');
      setLoading(false);
      return;
    }

    try {
      const [configRes, sectionsRes, linksRes] = await Promise.all([
        supabase.from('site_config').select('*').single(),
        supabase.from('sections').select('*').eq('is_visible', true).order('sort_order'),
        supabase.from('links').select('*').eq('is_visible', true).order('sort_order'),
      ]);

      if (!mountedRef.current || request !== requestRef.current) return;
      if (configRes.error) throw configRes.error;
      if (sectionsRes.error) throw sectionsRes.error;
      if (linksRes.error) throw linksRes.error;

      setConfig(configRes.data);

      const sectionData: SectionWithLinks[] = (sectionsRes.data || []).map((s: SectionRow) => ({
        id: s.id,
        title: s.title,
        emoji: getSectionIcon(s.id, s.emoji),
        sort_order: s.sort_order,
        links: (linksRes.data || [])
          .filter((l: LinkRow) => l.section_id === s.id)
          .map(toLinkItem),
      }));

      setSections(sectionData);
      setLoaded(true);
      setError(null);
      setLoading(false);
    } catch (err: unknown) {
      if (!mountedRef.current || request !== requestRef.current) return;
      console.error(`Failed to load public data (attempt ${attempt + 1}):`, err);

      if (attempt < MAX_RETRIES - 1) {
        timerRef.current = setTimeout(() => {
          if (mountedRef.current && request === requestRef.current) fetchAll(attempt + 1);
        }, RETRY_DELAY_MS * (attempt + 1));
        return;
      }

      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  }, [fallbackAllowed, initialData]);

  useEffect(() => {
    mountedRef.current = true;
    fetchAll(0);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchAll, retryCount]);

  // ===== Realtime subscriptions — auto-refetch on changes =====
  useEffect(() => {
    if (!supabase) return;

    const client = supabase;
    const channel = client
      .channel('public-data-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sections' }, () => {
        fetchAll(0);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'links' }, () => {
        fetchAll(0);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_config' }, () => {
        fetchAll(0);
      })
      .subscribe((status) => {
        // A connection can resume after initial fetch retries were exhausted,
        // and changes while disconnected need a fresh authoritative snapshot.
        if (status === 'SUBSCRIBED') fetchAll(0);
      });

    return () => {
      client.removeChannel(channel);
    };
  }, [fetchAll]);

  const retry = useCallback(() => {
    if (!supabase && !fallbackAllowed) { window.location.reload(); return; }
    setLoading(true);
    setError(null);
    setRetryCount((c) => c + 1);
  }, [fallbackAllowed]);

  return { config, sections, loading, error, retry, loaded, fallbackAllowed, hasInitialData: initialData !== null && initialData.source !== 'unavailable' };
}
